import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import './Navbar.css';
import { ArrowUpRightIcon, ChevronDownIcon, MenuIcon, CloseIcon } from '../Icons';
import logoImg from '../../assets/LogoDexa.png';
import { formatSlug } from '../../data/productsData';

// Modular Machines Categories & Sub-models Data
const productCategoriesData = [
  {
    id: 'panel-saw',
    name: 'Panel Saw',
    models: [
      { name: 'Panel Saw 3200 A', slug: 'ups-3200a' },
      { name: 'Panel Saw 3200 A.HV', slug: 'ups-3200a-hv' },
      { name: 'Panel Saw 3200 A++', slug: 'ups-3200a-plus' }
    ]
  },
  {
    id: 'auto-edgebander',
    name: 'Automatic Edgebander',
    models: [
      { name: 'UEB-240 S', slug: 'ueb-240-s' },
      { name: 'UEB- 310 C', slug: 'ueb-310-c' },
      { name: 'UEB-540 PC', slug: 'ueb-540-pc' },
      { name: 'UEB-640 PCGD', slug: 'ueb-640-pcgd' },
      { name: 'UEB-860 PC', slug: 'ueb-860-pc' },
      { name: 'UEB-860 PCGD', slug: 'ueb-860-pcgd' }
    ]
  },
  {
    id: 'manual-edgebander',
    name: 'Manual Edgebander',
    models: [
      { name: 'U-1006', slug: 'u-1006' },
      { name: 'U-1006-E', slug: 'u-1006-e' },
      { name: 'U-1006-45°', slug: 'u-1006-45' }
    ]
  },
  {
    id: 'beam-saw',
    name: 'Auto CNC Beam Saw',
    models: [
      { name: 'UBS-2800 AUTO', slug: 'ubs-2800-auto' },
      { name: 'UBS-3200 AUTO', slug: 'ubs-3200-auto' },
      { name: 'UBS-3200 AUTO+', slug: 'ubs-3200-auto-plus' }
    ]
  },
  {
    id: 'six-sided-drilling',
    name: 'CNC Six Sided Drilling',
    models: [
      { name: 'U-612KS', slug: 'u-612ks' }
    ]
  },
  {
    id: 'cold-press',
    name: 'Hydraulic Cold - Press',
    models: [
      { name: 'UCP-1001', slug: 'ucp-1001' },
      { name: 'UCP-1004', slug: 'ucp-1004' }
    ]
  },
  {
    id: 'hot-press',
    name: 'Hydraulic Hot - Press',
    models: [
      { name: 'UHP-1002', slug: 'uhp-1002' }
    ]
  },
  {
    id: 'multi-boring',
    name: 'Multi Boring',
    models: [
      { name: 'UMB 42', slug: 'umb-42' },
      { name: 'UMB 63 A', slug: 'umb-63-a' }
    ]
  },
  {
    id: 'cnc-router',
    name: 'CNC Router',
    models: [
      { name: 'UCR-1325 A', slug: 'ucr-1325-a' },
      { name: 'UCR-1325 SS', slug: 'ucr-1325-ss' }
    ]
  },
  {
    id: 'vacuum-press',
    name: 'Vacuum Press',
    models: [
      { name: 'UVP 1010', slug: 'uvp-1010' }
    ]
  },
  {
    id: 'spindle-moulder',
    name: 'Spindle Moulder',
    models: [
      { name: 'U 1004 / 1004A', slug: 'u-1004-1004a' }
    ]
  },
  {
    id: 'post-forming',
    name: 'Post Forming',
    models: [
      { name: 'UPF 1000', slug: 'upf-1000' }
    ]
  }
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [openMobileCategory, setOpenMobileCategory] = useState(null);
  const leaveTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY) {
          setShowNavbar(false);
        } else {
          setShowNavbar(true);
        }
      } else {
        setShowNavbar(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleMouseEnterProducts = () => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
    setProductsMenuOpen(true);
  };

  const handleMouseLeaveProducts = () => {
    leaveTimeoutRef.current = setTimeout(() => {
      setProductsMenuOpen(false);
    }, 220);
  };

  const toggleMobileCategory = (catId) => {
    setOpenMobileCategory(openMobileCategory === catId ? null : catId);
  };

  return (
    <header className={`navbar ${showNavbar ? '' : 'navbar-hidden'}`}>
      <div className="navbar-container">
        {/* Brand Logo */}
        <Link to="/" className="logo-brand" onClick={() => setMobileMenuOpen(false)}>
          <img src={logoImg} alt="DEXA Innovation" className="navbar-logo-img" />
        </Link>

        {/* Navigation Links */}
        <nav>
          <ul className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>

            {/* Home */}
            <li className="nav-item">
              <NavLink
                to="/"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </NavLink>
            </li>

            {/* About Us (Unlinked) */}
            <li className="nav-item">
              <span className="nav-link unlinked-nav-item">
                About Us
              </span>
            </li>

            {/* Products Dropdown */}
            <li
              className="nav-item has-flyout-dropdown"
              onMouseEnter={handleMouseEnterProducts}
              onMouseLeave={handleMouseLeaveProducts}
            >
              <div
                className="nav-link-wrapper"
                onClick={() => setProductsMenuOpen(!productsMenuOpen)}
              >
                <span className={`nav-link ${productsMenuOpen ? 'active' : ''}`}>
                  Products
                  <ChevronDownIcon size={12} color="currentColor" />
                </span>
              </div>

              {/* Primary Categories Flyout Box */}
              <div className={`flyout-dropdown-wrapper ${productsMenuOpen ? 'show' : ''}`}>
                <div className="flyout-col-primary">
                  <ul className="flyout-cat-list">
                    {productCategoriesData.map((cat, idx) => {
                      const isActive = activeCategoryIndex === idx;
                      const isNearBottom = idx >= 8;

                      return (
                        <li
                          key={cat.id}
                          className={`flyout-cat-item ${isActive ? 'active' : ''}`}
                          onMouseEnter={() => setActiveCategoryIndex(idx)}
                        >
                          <Link 
                            to={`/category/${cat.id}`} 
                            className="flyout-cat-name"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setProductsMenuOpen(false);
                            }}
                          >
                            {cat.name}
                          </Link>
                          <span className="flyout-chevron-right">›</span>

                          {/* Secondary Sub-models Box */}
                          {isActive && (
                            <div
                              className={`flyout-sub-box ${isNearBottom ? 'align-bottom' : ''}`}
                              onMouseEnter={handleMouseEnterProducts}
                            >
                              <div className="flyout-sub-header">
                                <span>{cat.name}</span>
                              </div>
                              <ul className="flyout-models-list">
                                {cat.models.map((model) => {
                                  const modelName = typeof model === 'string' ? model : model.name;
                                  const modelSlug = typeof model === 'string' ? formatSlug(model) : model.slug;
                                  return (
                                    <li key={modelName} className="flyout-model-item">
                                      <Link
                                        to={`/product/${modelSlug}`}
                                        className="flyout-model-link"
                                        onClick={() => {
                                          setMobileMenuOpen(false);
                                          setProductsMenuOpen(false);
                                        }}
                                      >
                                        <span>{modelName}</span>
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}

                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Mobile Drawer Accordion Version */}
              {mobileMenuOpen && (
                <div className="mobile-products-accordion">
                  {productCategoriesData.map((cat) => (
                    <div key={cat.id} className="mobile-cat-group">
                      <div 
                        className="mobile-cat-header"
                        onClick={() => toggleMobileCategory(cat.id)}
                      >
                        <span>{cat.name}</span>
                        <span className="mobile-cat-arrow">{openMobileCategory === cat.id ? '▴' : '▾'}</span>
                      </div>
                      {openMobileCategory === cat.id && (
                        <ul className="mobile-submodels-list">
                          {cat.models.map((model) => {
                            const modelName = typeof model === 'string' ? model : model.name;
                            const modelSlug = typeof model === 'string' ? formatSlug(model) : model.slug;
                            return (
                              <li key={modelName}>
                                <Link
                                  to={`/product/${modelSlug}`}
                                  className="mobile-model-link"
                                  onClick={() => {
                                    setMobileMenuOpen(false);
                                    setProductsMenuOpen(false);
                                  }}
                                >
                                  {modelName}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>

            {/* Contact */}
            <li className="nav-item">
              <NavLink
                to="/contact"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </NavLink>
            </li>

            {/* Mobile Get a Quote Button */}
            {mobileMenuOpen && (
              <li className="quote-btn-mobile-wrapper">
                <button
                  className="quote-btn"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => {
                    navigate('/contact');
                    setMobileMenuOpen(false);
                  }}
                >
                  <span>Get a Quote</span>
                  <span className="arrow-circle-badge">
                    <ArrowUpRightIcon size={16} color="#F9541B" />
                  </span>
                </button>
              </li>
            )}

          </ul>
        </nav>

        {/* Desktop Get a Quote Button */}
        <div className="quote-btn-desktop">
          <button className="quote-btn" onClick={() => navigate('/contact')}>
            <span>Get a Quote</span>
            <span className="arrow-circle-badge">
              <ArrowUpRightIcon size={16} color="#F9541B" />
            </span>
          </button>
        </div>

        {/* Mobile Hamburger Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle Menu"
        >
          {mobileMenuOpen ? <CloseIcon size={24} /> : <MenuIcon size={24} />}
        </button>
      </div>
    </header>
  );
};

export default Navbar;
